import { ICustomizationOptions, ICustomizations, Style } from "@/types";
import { cn } from "./styles";
import { AuthService } from "@saasbase-io/loginflow-websdk";

// Maps alignment string to CSS flex alignment
export function mapAlignment(alignment: string) {
  return (
    { start: "flex-start", center: "center", end: "flex-end" }[alignment] ||
    "flex-start"
  );
}

// Maps a button variant to its customization object
export function mapBtnToCustomizedStyle(
  variant:
    | "secondary"
    | "link"
    | "default"
    | "destructive"
    | "outline"
    | "ghost",
  customizations: ICustomizations,
): ICustomizationOptions | undefined {
  return {
    default: customizations.byType?.buttons?.defaultButton,
    secondary: customizations.byType?.buttons?.secondaryButton,
    link: customizations.byType?.buttons?.linkButton,
    destructive: customizations.byType?.buttons?.destructiveButton,
    ghost: customizations.byType?.buttons?.ghostButton,
    outline: customizations.byType?.buttons?.outlineButton,
  }[variant];
}

// Applies customizations to class and style, respecting root container, type, and ID
//TO DO: Check everything works with CSSStyleDeclaration as well
export function applyCustomizations(
  defaultClass: string,
  defaultStyle?: Style | CSSStyleDeclaration,
  typeCustomization?: ICustomizationOptions,
  elementId?: string,
  customizations?: ICustomizations,
  isRootContainer: boolean = false,
): { class: string; style: Style } {
  let finalClass = defaultClass || "";
  let finalStyle = { ...defaultStyle };

  // Apply global font from root container
  if (isRootContainer && customizations?.config?.font) {
    finalClass = cn(finalClass, customizations.config.font);
  }

  const mergeCustomization = (cust?: ICustomizationOptions) => {
    if (!cust) return;
    const { class: cClass, style: cStyle, font: cFont, rule = "merge" } = cust;
    if (rule === "override") {
      finalClass = cFont || cClass || "";
      finalStyle = cStyle || {};
    } else {
      finalClass = cn(finalClass, cClass, cFont);
      finalStyle = { ...finalStyle, ...cStyle };
    }
  };

  mergeCustomization(typeCustomization);

  if (elementId && customizations?.byId?.[elementId]) {
    mergeCustomization(customizations.byId[elementId]);
  }

  return { class: finalClass, style: finalStyle };
}

// * ====================================================================== * //

const RAND_STR_CHARS = "abcdefghijklmnopqrstuvwxyz0123456789";
const RAND_STR_LENGTH = 6;

function genRandStr(length?: number) {
  let rand = "";

  for (let i = 0; i < (length ?? RAND_STR_LENGTH); i++) {
    rand += RAND_STR_CHARS.charAt(
      Math.floor(Math.random() * RAND_STR_CHARS.length),
    );
  }

  return rand;
}

export const genRandSeed = () => genRandStr();

export function showDestructiveToast(message: string) {
  // Create the toast structure
  const toast = document.createElement("rtg-toast") as HTMLElement;
  const viewport = document.createElement("rtg-toast-view-port") as HTMLElement;
  viewport.part = "user-profile-toast";
  viewport.setAttribute("variant", "destructive");
  toast.dataset.state = "open";

  const contentWrapper = document.createElement("div");
  contentWrapper.className = "rtg-grid rtg-gap-1";

  const title = document.createElement("rtg-toast-title");
  title.textContent = "Uh oh! Something went wrong.";

  const description = document.createElement("rtg-toast-description");
  description.textContent = message;

  contentWrapper.appendChild(title);
  contentWrapper.appendChild(description);

  viewport.appendChild(contentWrapper);
  toast.appendChild(viewport);

  // Append to body
  document.body.appendChild(toast);

  // Click the trigger programmatically
  setTimeout(() => {
    toast.dataset.state = "open";
    viewport.dataset.state = "open";
  }, 10);

  // Auto dismiss after 5 seconds
  setTimeout(() => {
    if (toast.parentNode) {
      toast.parentNode.removeChild(toast);
    }
  }, 3.5 * 1000);
}

export async function waitForAuthConfigured(
  Auth: AuthService,
  timeoutMs = 5000,
) {
  const auth = (Auth as any) ?? (window as any).__saasbaseCoreSdk?.Auth;

  if (!auth) return;
  if ((auth as any).__configured) return;

  const when = (auth as any).__whenConfigured as Promise<void> | undefined;
  if (!when) return;

  await Promise.race([when, new Promise((res) => setTimeout(res, timeoutMs))]);
}
